import { attractionL1, L1_BY_KEY, normalizeSubcat, subcatsOf, type SubCat } from '../constants/taxonomy';
import { SubcategoryIcon } from './SubcategoryIcon';

/**
 * Icon-first subcategory tray — the control the Shortlist add-item form and
 * the accommodation form use. The selected name shows beside the label, so the
 * row itself can be icon only. `cats` overrides the list for pickers that span
 * more than one L1 (booking import's attraction picker covers entertainment too).
 */
export function SubcategoryPicker({ l1, cats, value, onChange, label = 'Category', hasError = false }: {
  l1?: string;
  cats?: Pick<SubCat, 'name' | 'icon'>[];
  value?: string;
  onChange: (name: string) => void;
  label?: string;
  hasError?: boolean;
}) {
  const options = cats ?? subcatsOf(l1 ?? '');
  const selected = normalizeSubcat(value);
  return (
    <div className="space-y-1.5" role="group" aria-label={label} aria-invalid={hasError || undefined}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 pr-1">
        <div className="ds-label">{label}</div>
        {value && <span className="ds-selection">{selected}</span>}
      </div>
      <div className={`flex gap-1.5 bg-app-faint/30 p-1 rounded-xl border overflow-x-auto no-scrollbar ${hasError ? 'border-rose-300' : 'border-app-border/40'}`}>
        {options.map(cat => (
          <button
            key={cat.name}
            type="button"
            aria-label={cat.name}
            aria-pressed={selected === cat.name}
            onClick={() => onChange(cat.name)}
            className={`flex-1 min-w-[48px] flex items-center justify-center py-2.5 rounded-lg transition-all shrink-0 ${
              selected === cat.name
                ? 'bg-white shadow-sm ring-1 ring-app-border/10 scale-[1.02]'
                : 'hover:bg-white'
            }`}
          >
            <SubcategoryIcon name={cat.name} l1={l1} icon={cat.icon} className="w-4 h-4 text-gold" />
          </button>
        ))}
      </div>
    </div>
  );
}

/** Both import wizards must let Other retain its Sightseeing/Leisure context. */
export function AttractionCategoryPicker({ data, onChange }: {
  data: { category?: string; categoryGroup?: string };
  onChange: (patch: { category: string; categoryGroup: string }) => void;
}) {
  const group = attractionL1(data);
  return <div className="space-y-2">
    <div className="flex flex-wrap gap-2" role="group" aria-label="Activity category">
      {['experiences', 'nightlife'].map(key => <button key={key} type="button"
        aria-pressed={group === key}
        className={`px-3 py-2 rounded-xl text-sm border ${group === key ? 'border-gold text-gold bg-gold/5' : 'border-app-border text-app-sub'}`}
        onClick={() => { if (key !== group) onChange({ categoryGroup: L1_BY_KEY[key].label, category: 'Other' }); }}>
        {L1_BY_KEY[key].label}
      </button>)}
    </div>
    <SubcategoryPicker l1={group} value={data.category}
      onChange={category => onChange({ category, categoryGroup: L1_BY_KEY[group].label })} />
  </div>;
}
